/**
 * PART D (William, 16 Oct 2026 about 10:45 PM, on a transcript whose raw lines read
 * `system: Intake captured: {"name":"", ...}` twice, the second filled in, the JSON running off the right
 * edge: "Clean this up").
 *
 * PURE: reads the message or transcript text into lines, and turns each `system: Intake captured: {...}` line
 * into a short labelled list. An intake line whose values are all empty is dropped; a blank value is left out;
 * a date is shown M/D/YYYY; true and false read Yes and No. Every other line is kept exactly as it is. A line
 * whose JSON cannot be read is kept as written (never dropped, never guessed at).
 *
 * NO em dashes (house voice rule). ASCII hyphens only.
 */

export interface IntakeField {
  label: string;
  value: string;
}

export type TranscriptPart =
  | { kind: 'line'; text: string }
  | { kind: 'intake'; fields: IntakeField[] };

/** The intake keys, in the order shown, with their labels (William's list). */
export const INTAKE_FIELDS: ReadonlyArray<{ key: string; label: string }> = [
  { key: 'name', label: 'Name' },
  { key: 'date_of_birth', label: 'Date of birth' },
  { key: 'request_type', label: 'Request' },
  { key: 'reason', label: 'Reason' },
  { key: 'language', label: 'Language' },
  { key: 'callback_number', label: 'Callback number' },
  { key: 'sms_preferred', label: 'Texts OK' },
  { key: 'sms_consent', label: 'Text consent' },
  { key: 'mailing_address', label: 'Mailing address' },
];

const INTAKE_RE = /^\s*system:\s*Intake captured:\s*(\{.*\})\s*$/i;

/** 1991-05-18 (or 1991-05-18T00:00:00) -> 5/18/1991; anything else as written. */
export function usDate(v: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[T ].*)?$/.exec(v.trim());
  if (!m) return v.trim();
  return `${Number(m[2])}/${Number(m[3])}/${m[1]}`;
}

/** 8164053225 or +18164053225 -> 816-405-3225; anything else as written. */
function phoneWords(v: string): string {
  const d = v.replace(/\D+/g, '');
  const ten = d.length === 11 && d.startsWith('1') ? d.slice(1) : d;
  return ten.length === 10 ? `${ten.slice(0, 3)}-${ten.slice(3, 6)}-${ten.slice(6)}` : v.trim();
}

/** test_help -> Test help; opt_out -> Opt out. */
function requestWords(v: string): string {
  const t = v.trim().replace(/[_-]+/g, ' ');
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : '';
}

function valueWords(key: string, raw: unknown): string {
  if (raw === null || raw === undefined) return '';
  if (typeof raw === 'boolean') return raw ? 'Yes' : 'No';
  if (typeof raw === 'object') {
    // A mailing address may come as an object: its non-empty parts, in order, joined by commas.
    const parts = Object.values(raw as Record<string, unknown>).map((x) => (x === null || x === undefined ? '' : String(x).trim())).filter(Boolean);
    return parts.join(', ');
  }
  const s = String(raw).trim();
  if (!s) return '';
  if (key === 'date_of_birth') return usDate(s);
  if (key === 'callback_number') return phoneWords(s);
  if (key === 'request_type') return requestWords(s);
  return s;
}

/** The labelled fields of one intake JSON, blanks left out; null when the text is not an intake object. */
export function intakeFields(json: string): IntakeField[] | null {
  let obj: unknown;
  try {
    obj = JSON.parse(json);
  } catch {
    return null;
  }
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return null;
  const o = obj as Record<string, unknown>;
  const out: IntakeField[] = [];
  for (const f of INTAKE_FIELDS) {
    const v = valueWords(f.key, o[f.key]);
    if (v) out.push({ label: f.label, value: v });
  }
  return out;
}

/** The text as parts: plain lines, and each intake line as its fields (an all-empty intake is dropped). */
export function transcriptParts(text: string): TranscriptPart[] {
  const out: TranscriptPart[] = [];
  for (const line of String(text ?? '').split(/\r?\n/)) {
    const m = INTAKE_RE.exec(line);
    if (m) {
      const fields = intakeFields(m[1]!);
      if (fields === null) {
        out.push({ kind: 'line', text: line });
        continue;
      }
      if (fields.length) out.push({ kind: 'intake', fields });
      continue;
    }
    out.push({ kind: 'line', text: line });
  }
  return out;
}

/** The intake's name and date of birth (YYYY-MM-DD as captured), from the LAST filled intake line, for Find Member. */
export function intakeIdentity(text: string): { name: string; dob: string; phone: string } {
  let name = '';
  let dob = '';
  let phone = '';
  for (const line of String(text ?? '').split(/\r?\n/)) {
    const m = INTAKE_RE.exec(line);
    if (!m) continue;
    try {
      const o = JSON.parse(m[1]!) as Record<string, unknown>;
      const n = typeof o['name'] === 'string' ? o['name'].trim() : '';
      const d = typeof o['date_of_birth'] === 'string' ? o['date_of_birth'].trim() : '';
      const p = typeof o['callback_number'] === 'string' ? o['callback_number'].trim() : '';
      if (n) name = n;
      if (d) dob = d;
      if (p) phone = p;
    } catch {
      /* a line that is not JSON gives nothing */
    }
  }
  return { name, dob, phone };
}
